import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Toolbar,
  Typography,
  FormHelperText,
  Stack,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

const getActiveCriterionFromFilters = (filters) => {
  if (filters.SAID !== '') return 'SAID'
  if (filters.memberNo !== '') return 'memberNo'
  if (filters.patientName !== '') return 'patientName'
  if (filters.monthYear !== '') return 'monthYear'
  if (filters.recentDays !== '') return 'recentDays'
  return ''
}

const readStoredFilters = (storageKey, defaultFilters) => {
  try {
    const raw = localStorage.getItem(storageKey)
    return raw ? { ...defaultFilters, ...JSON.parse(raw) } : defaultFilters
  } catch {
    return defaultFilters
  }
}

const SharedVisitFilterModal = ({
  open,
  onClose,
  onChanged,
  storageKey,
  title,
  defaultFilters,
  showActiveCriterionHelper = false,
  notifyOnClear = true,
  hasResults = false,
}) => {
  const [filters, setFilters] = useState(defaultFilters)
  const [initialFilters, setInitialFilters] = useState(defaultFilters)
  const [activeCriterion, setActiveCriterion] = useState('')

  const loadStoredFilters = () => {
    const storedFilters = readStoredFilters(storageKey, defaultFilters)
    setFilters(storedFilters)
    setInitialFilters(storedFilters)
    setActiveCriterion(getActiveCriterionFromFilters(storedFilters))
  }

  const shouldNotifyChange = (nextFilters) => {
    const filtersChanged = JSON.stringify(nextFilters) !== JSON.stringify(initialFilters)
    return filtersChanged || !hasResults
  }

  useEffect(() => {
    loadStoredFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (open) {
      loadStoredFilters()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleFocus = (field) => () => {
    setActiveCriterion(field)
  }

  const handleChange = (field) => (event) => {
    setActiveCriterion(field)
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleClear = () => {
    setFilters(defaultFilters)
    setActiveCriterion('')
    localStorage.setItem(storageKey, JSON.stringify(defaultFilters))
    setInitialFilters(defaultFilters)
    if (notifyOnClear && typeof onChanged === 'function' && shouldNotifyChange(defaultFilters)) {
      onChanged(defaultFilters)
    }
  }

  const handleSubmit = () => {
    let resolvedCriterion = activeCriterion
    if (!resolvedCriterion) {
      resolvedCriterion = getActiveCriterionFromFilters(filters)
    }

    const sanitizedFilters = { ...defaultFilters }
    if (resolvedCriterion === 'SAID') {
      sanitizedFilters.SAID = String(filters.SAID || '').trim()
    } else if (resolvedCriterion === 'memberNo') {
      sanitizedFilters.memberNo = String(filters.memberNo || '').trim()
    } else if (resolvedCriterion === 'patientName') {
      sanitizedFilters.patientName = String(filters.patientName || '').trim()
    } else if (resolvedCriterion === 'monthYear') {
      sanitizedFilters.monthYear = filters.monthYear
    } else if (resolvedCriterion === 'recentDays') {
      sanitizedFilters.recentDays = filters.recentDays
    }

    localStorage.setItem(storageKey, JSON.stringify(sanitizedFilters))

    if (typeof onChanged === 'function' && shouldNotifyChange(sanitizedFilters)) {
      onChanged(sanitizedFilters)
    }

    setFilters(sanitizedFilters)
    setInitialFilters(sanitizedFilters)
    setActiveCriterion(getActiveCriterionFromFilters(sanitizedFilters))

    onClose()
  }

  const activeFilterLabel =
    activeCriterion === 'SAID'
      ? 'Patient ID / Passport'
      : activeCriterion === 'memberNo'
      ? 'Membership Number'
      : activeCriterion === 'patientName'
      ? 'Patient Name'
      : activeCriterion === 'monthYear'
      ? 'Month and Year of Visit'
      : activeCriterion === 'recentDays'
      ? 'Number of Days'
      : ''

  const isIdentityCriterionActive = ['SAID', 'memberNo', 'patientName'].includes(activeCriterion)
  const isMonthYearNeutralized = isIdentityCriterionActive || activeCriterion === 'recentDays'
  const isRecentDaysNeutralized = isIdentityCriterionActive || activeCriterion === 'monthYear'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: '#f1d6b8',
        },
      }}
    >
      <Toolbar variant="dense">
        <IconButton
          autoFocus
          edge="start"
          color="inherit"
          onClick={handleSubmit}
          aria-label="close"
          size="large"
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h6">
          <DialogTitle>{title}</DialogTitle>
        </Typography>
      </Toolbar>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {showActiveCriterionHelper && activeFilterLabel !== '' && (
            <FormHelperText>
              Active filter criterion: {activeFilterLabel}
            </FormHelperText>
          )}

          <TextField
            label="Patient ID / Passport (SAID)"
            fullWidth
            value={filters.SAID}
            onChange={handleChange('SAID')}
            onFocus={handleFocus('SAID')}
            placeholder="Enter patient ID or passport number"
          />

          <TextField
            label="Membership Number"
            fullWidth
            value={filters.memberNo}
            onChange={handleChange('memberNo')}
            onFocus={handleFocus('memberNo')}
            placeholder="Enter membership number"
          />

          <TextField
            label="Patient Name (Surname)"
            fullWidth
            value={filters.patientName}
            onChange={handleChange('patientName')}
            onFocus={handleFocus('patientName')}
            placeholder="Enter patient surname"
          />

          <TextField
            label="Month and Year of Visit"
            fullWidth
            type="month"
            value={filters.monthYear}
            onChange={handleChange('monthYear')}
            onFocus={handleFocus('monthYear')}
            disabled={isMonthYearNeutralized}
            InputLabelProps={{ shrink: true }}
            helperText={isMonthYearNeutralized ? 'Neutralized by selected filter criterion' : ''}
            sx={
              isMonthYearNeutralized
                ? {
                    '& .MuiOutlinedInput-root': { bgcolor: 'action.disabledBackground' },
                    '& .MuiInputBase-input': { color: 'text.disabled' },
                  }
                : undefined
            }
          />

          <TextField
            label="Recent days (e.g. 60)"
            fullWidth
            type="number"
            inputProps={{ min: 1, max: 365 }}
            value={filters.recentDays}
            onChange={handleChange('recentDays')}
            onFocus={handleFocus('recentDays')}
            disabled={isRecentDaysNeutralized}
            helperText={isRecentDaysNeutralized ? 'Neutralized by selected filter criterion' : ''}
            placeholder="Number of recent days to show"
            sx={
              isRecentDaysNeutralized
                ? {
                    '& .MuiOutlinedInput-root': { bgcolor: 'action.disabledBackground' },
                    '& .MuiInputBase-input': { color: 'text.disabled' },
                  }
                : undefined
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleClear} color="warning">
          Clear Filters
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SharedVisitFilterModal
